import { Fragment, ReactNode, useMemo } from 'react'
import { Link, Typography } from '@carto/meridian-ds/components'

import { WEB_URL_SEARCH_REGEXP } from 'features/common/utils/urlValidation'
import { parseText, TextParserTagDef } from 'features/common/utils/textParser'
import { MENTION_REGEXP } from 'features/collaboration/utils/mentions'
import { forceAbsoluteUrl } from 'features/common/utils/urlUtils'
import { CommentResponseActionType, CommentUser } from 'features/collaboration/types'

const getCommentTagParsers = (commentUsers: CommentUser[], isDark?: boolean): TextParserTagDef<ReactNode>[] => [
  {
    pattern: MENTION_REGEXP,
    callback: (match) => {
      const email = match[0].substring(1)
      const user = commentUsers.find((u) => u?.email === email)
      const text = user ? email : 'Deleted User'
      const inactive = !user || user.noAccess
      return (
        <>
          @
          <Typography
            color={isDark ? 'common.white' : inactive ? 'text.secondary' : 'primary'}
            component="span"
            variant="inherit"
          >
            {text}
          </Typography>
        </>
      )
    }
  },
  {
    pattern: WEB_URL_SEARCH_REGEXP,
    callback: (match) => {
      const link = match[0]
      const href = forceAbsoluteUrl(link)
      return (
        <Link href={href} external fontWeight="inherit" color={isDark ? 'common.white' : 'primary'}>
          {match[0]}
        </Link>
      )
    }
  }
]

export type CommentContentProps = {
  actionType?: CommentResponseActionType
  content: string
  maxLines?: number
  size?: 'small'
  disabled?: boolean
  isDark?: boolean
  users: CommentUser[]
}

export default function CommentContent({
  content,
  actionType = CommentResponseActionType.Reply,
  maxLines,
  size,
  disabled,
  isDark,
  users
}: CommentContentProps) {
  const isSmall = size === 'small'
  const isReply = actionType === CommentResponseActionType.Reply
  const renderedContent = useMemo(() => {
    if (!isReply) {
      return <em>{content}</em>
    }

    const parsers = getCommentTagParsers(users || [], isDark)
    return parseText(content, parsers).map((node, index) => {
      return <Fragment key={index}>{node}</Fragment>
    })
  }, [isReply, content, users, isDark])

  const variant = isSmall ? 'caption' : 'body2'
  const color = !isReply || disabled ? 'text.secondary' : isDark ? 'common.white' : 'text.primary'
  return (
    <Typography
      variant={variant}
      color={color}
      sx={{
        whiteSpace: 'pre-wrap',
        overflowX: 'clip',
        overflowWrap: 'break-word'
      }}
      style={
        maxLines
          ? {
              overflowY: 'hidden',
              overflowX: 'clip',
              display: '-webkit-box',
              WebkitLineClamp: maxLines,
              WebkitBoxOrient: 'vertical'
            }
          : undefined
      }
    >
      {renderedContent}
    </Typography>
  )
}
